import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { WORKSPACE_LAYER } from '../MerchantDoc/shell';
import { btn } from './ui';

// ── A row's actions, as one ⋯ menu (owner 2026-11-02, "Consolidated Document Actions Dropdown") ───────────────
// One trigger in the Actions column instead of a strip of buttons. Sales' six lists are its first callers
// (panes/Documents.tsx); the Purchases and Expenses lists adopt it later, so ⛔ THE ITEMS ARE THE CALLER'S: nothing here
// knows what a document is. The caller may hand a `read` — the server's verdict for THIS row, read once each time the
// menu opens, because a click the door would refuse pages the admins (server/src/index.ts) — and gets the answer back
// in `items`. A caller with nothing to ask (a read-only seat) hands none, and nothing is read.
//
// ⛔ A WITHHELD ITEM IS GREYED, WITH ITS ONE SENTENCE. EXCEPTION, owner 2026-11-02: JournalBook.tsx RowActions (TL-24,
// 「工作台不留灰菜单」) says the sentence alone and draws no item. This requirement names the actions a document has, so a
// document whose Edit is shut still shows Edit, greyed, with why under it — what the portal's own RowMenu has done since
// batch 11 R3 (ClientPortal/index.tsx). It stays focusable, so a keyboard reader reaches the sentence; a pick does nothing.
//
// ⛔ PORTALLED AND FIXED, off the trigger's own rect: the lists sit in a card with `overflowX: auto`, which would clip
// anything absolute. Above WORKSPACE_LAYER, because the list stays reachable by keyboard under a full-screen window
// (testUnsavedGuard, testFsTail T2-2). A panel pinned to a rect must not linger where the trigger no longer is, so a
// scroll anywhere else, or a resize, closes it. React events still bubble through the portal, so both ends stop them:
// a click in here is never the row's click.
//
// A list's Status dropdown (listCells.tsx StatusCell, owner 2026-11-02) is this same menu on a trigger of its own: the same read, the same
// greyed sentences, and a pick that does what the same item on the ⋯ does. That trigger carries no data-row-menu: the ⋯ stays the row's.

export type RowMenuItem = {
  /** Stable name, on the DOM as data-row-menu-item (the suites find an item by it). */
  key: string;
  /** The tabler class, `ti-pencil`. */
  icon: string;
  label: React.ReactNode;
  onPick?: () => void;
  /** Withheld: greyed, this one sentence under the label, and a pick does nothing. */
  withheld?: React.ReactNode;
  /** The hover text: what the item does. ⛔ Not on a withheld item: its sentence is on it, in the reader's language. */
  title?: string;
  /** Not yet decidable (the verdict is in flight, another row's PDF is rendering): greyed, with a spinner. */
  waiting?: boolean;
  /** Takes the document out of the list (Void, Delete): its words in red, unless it is greyed. */
  danger?: boolean;
  /** Extra DOM attributes, e.g. the data-ef-btn the entries panel's opener has always carried. */
  attrs?: Record<string, string>;
};

/** A plain line in place of the items (the row has gone from under the menu). */
export type RowMenuNote = { key: string; note: React.ReactNode };
export type RowMenuEntry = RowMenuItem | RowMenuNote;
const isNote = (e: RowMenuEntry): e is RowMenuNote => 'note' in e;

export type RowMenuPlace = { top: number; left: number; maxHeight: number; up: boolean };
const GAP = 4;
const EDGE = 8;
/** How long after a dismissing press its click may still come: a phone's arrives some 300ms after the touch. */
const DISMISS_CLICK_MS = 700;

/** Where the panel opens: under the trigger with their right edges aligned; over it when it would pass the bottom edge
 *  and there is more room above; never off either side. It scrolls inside itself when neither side holds it all. */
export function placeRowMenu(trigger: { top: number; bottom: number; right: number }, panel: { width: number; height: number }, view: { width: number; height: number }): RowMenuPlace {
  const roomBelow = view.height - EDGE - (trigger.bottom + GAP);
  const roomAbove = trigger.top - GAP - EDGE;
  const up = panel.height > roomBelow && roomAbove > roomBelow;
  const left = Math.max(EDGE, Math.min(trigger.right - panel.width, view.width - panel.width - EDGE));
  return up
    ? { top: Math.max(EDGE, trigger.top - GAP - panel.height), left, maxHeight: roomAbove, up }
    : { top: trigger.bottom + GAP, left, maxHeight: roomBelow, up };
}

/** The press that dismissed a menu goes on to be a click, and a row under it opens on a click. That click ends here, once, unless it is
 *  on another row's ⋯, Status dropdown or related-documents icon or dot (relatedDocs.tsx, which dismisses the same way), which is what a reader moving from one
 *  to the next is pressing. A press that makes no click (a drag, a touch that scrolled) leaves nothing behind for the next real one. */
export function swallowDismissingClick() {
  const off = () => { document.removeEventListener('click', swallow, true); window.clearTimeout(timer); };
  const swallow = (c: Event) => {
    off();
    if (c.target instanceof Element && c.target.closest('[data-row-menu], [data-related-docs], [data-related-dot], [data-status-menu]')) return;
    c.stopPropagation();
    c.preventDefault();
  };
  document.addEventListener('click', swallow, true);
  const timer = window.setTimeout(off, DISMISS_CLICK_MS);
}

export default function RowMenu<V = unknown>({ items, read, busy, label, trigger }: {
  /** What the menu lists now. `verdict` is null while `read` is in flight, and always for a caller with no `read`. */
  items: (verdict: V | null, readError: string) => RowMenuEntry[];
  read?: () => Promise<V>;
  /** One of this row's actions is running (a PDF rendering): the trigger spins instead of its dots. */
  busy?: boolean;
  /** The trigger's name for a screen reader and on hover; the Actions column's by default. */
  label?: string;
  /** What the trigger looks like, in place of the ⋯: its own content, style and DOM attributes. */
  trigger?: { content: React.ReactNode; style: React.CSSProperties; attrs: Record<string, string>; title?: string };
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<RowMenuPlace | null>(null);
  const [verdict, setVerdict] = useState<V | null>(null);
  const [readError, setReadError] = useState('');
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const focused = useRef(false);
  const readRef = useRef(read);
  readRef.current = read;

  const show = () => { setVerdict(null); setReadError(''); setPos(null); focused.current = false; setOpen(true); };
  const close = (refocus: boolean) => { setOpen(false); if (refocus) btnRef.current?.focus(); };

  useEffect(() => {
    if (!open) return;
    const away = (e: Event) => {
      const n = e.target as Node;
      if (btnRef.current?.contains(n) || panelRef.current?.contains(n)) return;
      setOpen(false);
      swallowDismissingClick();
    };
    // Capture, so Esc answers the menu alone: the window behind it listens on the document too (utils/escLayer.ts).
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); close(true); } };
    // Capture catches every scrolling container; the panel's own scroll is not one of the reasons to close it.
    const bail = (e: Event) => { if (!(e.target instanceof Node && panelRef.current?.contains(e.target))) setOpen(false); };
    const gone = () => setOpen(false);
    document.addEventListener('mousedown', away);
    document.addEventListener('touchstart', away);
    document.addEventListener('keydown', esc, true);
    window.addEventListener('scroll', bail, true);
    window.addEventListener('resize', gone);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('touchstart', away);
      document.removeEventListener('keydown', esc, true);
      window.removeEventListener('scroll', bail, true);
      window.removeEventListener('resize', gone);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !readRef.current) return;
    let alive = true;
    readRef.current()
      .then(v => { if (alive) setVerdict(v); })
      .catch((e: any) => { if (alive) setReadError(String(e?.message || e)); });
    return () => { alive = false; };
  }, [open]);

  // Measured before paint, and again when the answer changes what the panel holds (a sentence makes it taller).
  useLayoutEffect(() => {
    if (!open) return;
    const r = btnRef.current?.getBoundingClientRect();
    const p = panelRef.current;
    if (!r || !p) return;
    const box = p.getBoundingClientRect();
    setPos(placeRowMenu(r, { width: box.width, height: Math.max(box.height, p.scrollHeight) }, { width: window.innerWidth, height: window.innerHeight }));
  }, [open, verdict, readError]);

  const entries = () => Array.from(panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') || []);
  // Into the menu once it is visible (a hidden element takes no focus), on the first item, withheld or not.
  useEffect(() => {
    if (!open || !pos || focused.current) return;
    focused.current = true;
    entries()[0]?.focus();
  }, [open, pos]);

  const keys = (e: React.KeyboardEvent) => {
    const list = entries();
    const at = list.indexOf(document.activeElement as HTMLElement);
    const go = (i: number) => { e.preventDefault(); list[(i + list.length) % (list.length || 1)]?.focus(); };
    if (e.key === 'ArrowDown') go(at + 1);
    else if (e.key === 'ArrowUp') go(at < 0 ? list.length - 1 : at - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(list.length - 1);
    // The panel hangs off <body>: Tab goes on from the trigger, not from the end of the page.
    else if (e.key === 'Tab') { e.preventDefault(); close(true); }
  };

  const pick = (it: RowMenuItem) => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (it.withheld || it.waiting || !it.onPick) return;
    close(true);
    it.onPick();
  };
  // One highlight, which is the focus: the mouse moves it too, so a hovered item and the focused one are never two.
  const tone = (on: boolean) => (e: React.SyntheticEvent<HTMLElement>) => { e.currentTarget.style.background = on ? 'var(--bg-app)' : 'transparent'; };
  const row: React.CSSProperties = { ...btn, display: 'flex', alignItems: 'flex-start', width: '100%', border: 'none', background: 'transparent', textAlign: 'left', padding: '8px 10px', whiteSpace: 'normal' };
  const why: React.CSSProperties = { display: 'block', fontSize: 11.5, fontWeight: 400, color: 'var(--text-secondary)', lineHeight: 1.45, marginTop: 2 };
  const name = label || t('aw_col_actions');
  const list = open ? items(verdict, readError) : [];
  const firstDanger = list.findIndex(it => !isNote(it) && !!it.danger);

  return (
    <>
      <button ref={btnRef} type="button" {...(trigger ? trigger.attrs : { 'data-row-menu': '' })} title={trigger?.title || name} aria-label={name} aria-haspopup="menu" aria-expanded={open}
        onClick={e => { e.stopPropagation(); if (open) close(false); else show(); }}
        onKeyDown={e => { if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); if (!open) show(); } }}
        style={trigger ? trigger.style : { ...btn, padding: '4px 9px', fontSize: 14, lineHeight: 1, ...(open ? { background: 'var(--bg-app)' } : {}) }}>
        {trigger ? trigger.content : <i className={busy ? 'ti ti-loader' : 'ti ti-dots'} aria-hidden />}
      </button>
      {open && createPortal(
        <div ref={panelRef} role="menu" aria-label={name} aria-busy={!!read && verdict === null && !readError}
          data-row-menu-panel={pos?.up ? 'up' : 'down'} onClick={e => e.stopPropagation()} onKeyDown={keys}
          style={{
            position: 'fixed', top: pos?.top ?? 0, left: pos?.left ?? 0, visibility: pos ? 'visible' : 'hidden', zIndex: WORKSPACE_LAYER + 1,
            // max-content: a fixed box left to shrink-to-fit is as wide as the room right of `left`, so measuring it and placing it would chase each other.
            width: 'max-content', minWidth: 190, maxWidth: Math.min(320, window.innerWidth - 2 * EDGE), maxHeight: pos?.maxHeight, overflowY: 'auto', boxSizing: 'border-box',
            background: 'var(--bg-surface)', border: '1px solid var(--border-strong)', borderRadius: 10, boxShadow: 'var(--shadow-md)', padding: 5,
          }}>
          {list.map((it, i) => {
            if (isNote(it)) return <div key={it.key} data-row-menu-note={it.key} style={{ ...why, padding: '6px 10px', marginTop: 0 }}>{it.note}</div>;
            const shut = !!it.withheld || !!it.waiting;
            return (
              <React.Fragment key={it.key}>
                {/* A rule above what takes the document out of the list, greyed or not: a greyed one has no red to mark it. */}
                {i > 0 && i === firstDanger && <div role="separator" data-row-menu-rule="" style={{ height: 1, background: 'var(--border-strong)', margin: '4px 6px' }} />}
                <button type="button" role="menuitem" tabIndex={-1} title={it.title} data-row-menu-item={it.key} aria-disabled={shut || undefined} {...(it.attrs || {})}
                  onClick={pick(it)} onMouseEnter={shut ? undefined : e => e.currentTarget.focus()} onFocus={tone(true)} onBlur={tone(false)}
                  style={{ ...row, ...(shut ? { color: 'var(--text-tertiary)', cursor: 'not-allowed' } : it.danger ? { color: '#B42318' } : {}) }}>
                  <i className={`ti ${it.waiting ? 'ti-loader' : it.icon}`} aria-hidden style={{ marginTop: 1, flexShrink: 0 }} />
                  <span style={{ minWidth: 0 }}>
                    {it.label}
                    {it.withheld && <span data-row-menu-why={it.key} style={why}>{it.withheld}</span>}
                  </span>
                </button>
              </React.Fragment>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
}
